export enum FeatureType {
  AUDIT_LOG = 'audit_log',
  PR_INSIGHTS = 'pr_insights',
  WEBHOOKS = 'webhooks',
  MONOREPOS = 'monorepos',
  SLACK_INTEGRATION = 'slack_integration',
  JIRA_INTEGRATION = 'jira_integration',
  OWASP_TOP_10 = 'owasp_top_10',
  SANS_TOP_25 = 'sans_top_25',
  LICENSE_COMPLIANCE = 'license_compliance',
  DEPENDENCIES = 'dependencies',
  SCA_VULNERABILITIES = 'sca_vulnerabilities',
  DYNAMIC_RISK = 'dynamic_risk',
  SECRETS_DETECTION = 'secrets_detection'
}

export enum PlanTypes {
  FREE = 'free',
  STARTER = 'starter',
  BUSINESS = 'business',
  ENTERPRISE = 'enterprise'
}

export enum V2PlanTypes {
  OPEN_SOURCE = 'open_source',
  TRIAL = 'trial',
  TEAM = 'team',
  ENTERPRISE = 'enterprise'
}

export interface FeatureProperties {
  title: string
  description?: string
  minRequiredPlan?: PlanTypes
  minRequiredPlanV2?: V2PlanTypes
}

export const featureMap: Record<FeatureType, FeatureProperties> = {
  [FeatureType.AUDIT_LOG]: {
    title: 'Audit Log',
    minRequiredPlan: PlanTypes.BUSINESS,
    minRequiredPlanV2: V2PlanTypes.TEAM
  },
  [FeatureType.PR_INSIGHTS]: {
    title: 'PR Insights',
    description:
      "PR insights provide structured feedback on the code's quality and security by connecting the dots between issues detected, so you or your AI coding agent can see the big picture.",
    minRequiredPlanV2: V2PlanTypes.TEAM
  },
  [FeatureType.WEBHOOKS]: {
    title: 'Webhooks',
    description:
      'Send real-time notifications to your services when events happen in DeepSource, so you can build custom integrations and workflows.',
    minRequiredPlan: PlanTypes.FREE,
    minRequiredPlanV2: V2PlanTypes.TEAM
  },
  [FeatureType.MONOREPOS]: {
    title: 'Monorepos',
    description:
      'Manage and analyze monorepo projects with unified configuration, letting you track code quality across all packages.',
    minRequiredPlan: PlanTypes.BUSINESS,
    minRequiredPlanV2: V2PlanTypes.TEAM
  },
  [FeatureType.SLACK_INTEGRATION]: {
    title: 'Slack Integration',
    description:
      'Get notified about new issues, analysis results, and code quality changes directly in your Slack channels.',
    minRequiredPlan: PlanTypes.FREE,
    minRequiredPlanV2: V2PlanTypes.TEAM
  },
  [FeatureType.JIRA_INTEGRATION]: {
    title: 'Jira Integration',
    description:
      'Create and track Jira issues directly from DeepSource findings, keeping your development workflow connected.',
    minRequiredPlan: PlanTypes.FREE,
    minRequiredPlanV2: V2PlanTypes.TEAM
  },
  [FeatureType.OWASP_TOP_10]: {
    title: 'OWASP Top 10 Report',
    description:
      "Track your codebase's compliance with OWASP Top 10 vulnerability categories and monitor progress over time.",
    minRequiredPlan: PlanTypes.FREE,
    minRequiredPlanV2: V2PlanTypes.TEAM
  },
  [FeatureType.SANS_TOP_25]: {
    title: 'SANS Top 25 Report',
    description:
      "Track your codebase's compliance with SANS Top 25 vulnerability categories and monitor progress over time.",
    minRequiredPlan: PlanTypes.FREE,
    minRequiredPlanV2: V2PlanTypes.TEAM
  },
  [FeatureType.LICENSE_COMPLIANCE]: {
    title: 'License Compliance',
    description:
      'Define license safety policies for your workspace to ensure third-party dependencies meet your compliance requirements.',
    minRequiredPlan: PlanTypes.FREE,
    minRequiredPlanV2: V2PlanTypes.TEAM
  },
  [FeatureType.DEPENDENCIES]: {
    title: 'Dependencies',
    description:
      'Track and manage third-party dependencies, monitor for known vulnerabilities, and keep your supply chain secure.',
    minRequiredPlan: PlanTypes.FREE,
    minRequiredPlanV2: V2PlanTypes.TEAM
  },
  [FeatureType.SCA_VULNERABILITIES]: {
    title: 'SCA Vulnerability Policies',
    description:
      'Configure vulnerability gates and PR blocking rules to control which dependency vulnerabilities are flagged in your workflow.',
    minRequiredPlan: PlanTypes.FREE,
    minRequiredPlanV2: V2PlanTypes.TEAM
  },
  [FeatureType.DYNAMIC_RISK]: {
    title: 'Dynamic Risk',
    description:
      'Customize how vulnerability risk scores are calculated by adjusting weights for CVSS, EPSS, and reachability factors.',
    minRequiredPlan: PlanTypes.FREE,
    minRequiredPlanV2: V2PlanTypes.TEAM
  },
  [FeatureType.SECRETS_DETECTION]: {
    title: 'Secrets Detection',
    description:
      'Scan code and configuration files to find accidentally exposed sensitive data like API keys, passwords, and tokens.',
    minRequiredPlanV2: V2PlanTypes.TEAM
  }
}
